import moment, { Moment } from 'moment';
import { PreferredServiceDay } from 'src/components/ServiceRequestForm/TimeSlot/timeSlotHelpers';
import {
  AvailabilityMode,
  IAvailableServiceTimeDay,
  IAvailableServiceTimeframe,
} from 'src/services/api/UserManagement/apiContracts';

const SCHEDULED_SLOT_FORMAT: string = 'YYYY-MM-DD HH:mm A';

const MINUTES_PER_HOUR: number = 60;
const NOON_MINUTES: number = 12 * MINUTES_PER_HOUR;
const EVENING_START_MINUTES: number = 17 * MINUTES_PER_HOUR;

const slotWindowContains = (
  slot: string,
  scheduledMinutesOfDay: number
): boolean => {
  const normalizedSlot: string = slot.toLowerCase();

  // Free-text "Other" windows can't be evaluated; treat them as a match so we
  // don't nag the scheduler over something we can't verify.
  if (normalizedSlot.startsWith('other') || normalizedSlot === 'all day') {
    return true;
  }

  if (normalizedSlot.startsWith('morning')) {
    return scheduledMinutesOfDay < NOON_MINUTES;
  }

  if (normalizedSlot.startsWith('afternoon')) {
    return (
      scheduledMinutesOfDay >= NOON_MINUTES &&
      scheduledMinutesOfDay < EVENING_START_MINUTES
    );
  }

  if (normalizedSlot.startsWith('evening')) {
    return scheduledMinutesOfDay >= EVENING_START_MINUTES;
  }

  // legacy/workaround: Albaster stores clock ranges instead of named windows.
  if (normalizedSlot.includes('7:30 - 11:30')) {
    const windowStart: number = 7 * MINUTES_PER_HOUR + 30;
    const windowEnd: number = 11 * MINUTES_PER_HOUR + 30;

    return (
      scheduledMinutesOfDay >= windowStart && scheduledMinutesOfDay <= windowEnd
    );
  }

  if (normalizedSlot.includes('11:30 - 3:30')) {
    const windowStart: number = 11 * MINUTES_PER_HOUR + 30;
    const windowEnd: number = 15 * MINUTES_PER_HOUR + 30;

    return (
      scheduledMinutesOfDay >= windowStart && scheduledMinutesOfDay <= windowEnd
    );
  }

  // Unknown slot labels can't be evaluated; stay lenient.
  return true;
};

const isWithinPreferredTimes = (
  scheduledAt: string,
  preferredServiceTimes: PreferredServiceDay[]
): boolean => {
  const scheduledMoment: Moment = moment(scheduledAt, SCHEDULED_SLOT_FORMAT);

  if (!scheduledMoment.isValid()) {
    return true;
  }

  const scheduledDay: string = scheduledMoment.format('dddd');

  const preferredDay: PreferredServiceDay | undefined =
    preferredServiceTimes.find(
      (candidate: PreferredServiceDay) => candidate.day === scheduledDay
    );

  if (!preferredDay) {
    return false;
  }

  const scheduledMinutesOfDay: number =
    scheduledMoment.hours() * MINUTES_PER_HOUR + scheduledMoment.minutes();

  return preferredDay.slots.some((slot: string) =>
    slotWindowContains(slot, scheduledMinutesOfDay)
  );
};

// The BE resolves tenant schedules server-side now; the FE only tells it which
// slot layout the homeowner's preferences use. Named windows (Morning/...)
// get the per-timeframe grid; clock ranges and free-text get the all-day grid.
const NAMED_WINDOW_PREFIXES: string[] = ['morning', 'afternoon', 'evening'];

const toAvailabilityMode = (
  preferredServiceTimes: PreferredServiceDay[]
): AvailabilityMode => {
  const firstPreferredSlot: string | undefined =
    preferredServiceTimes[0]?.slots[0];

  if (!firstPreferredSlot) {
    return 'AllDay';
  }

  const normalizedSlot: string = firstPreferredSlot.toLowerCase();

  const isNamedWindow: boolean = NAMED_WINDOW_PREFIXES.some(
    (prefix: string) => normalizedSlot.startsWith(prefix)
  );

  return isNamedWindow ? 'Timeframes' : 'AllDay';
};

// legacy/workaround: the v2 endpoint groups timeframes by first occurrence in
// the provider's schedule, so a day can come back Evening-first.
const TIMEFRAME_DISPLAY_ORDER: string[] = ['morning', 'afternoon', 'evening'];

const timeframeDisplayRank = (timeframeName: string): number => {
  const normalizedName: string = timeframeName.toLowerCase();

  const rank: number = TIMEFRAME_DISPLAY_ORDER.indexOf(normalizedName);

  if (rank === -1) {
    return TIMEFRAME_DISPLAY_ORDER.length;
  }

  return rank;
};

const byTimeframeDisplayOrder = (
  first: IAvailableServiceTimeframe,
  second: IAvailableServiceTimeframe
): number => {
  const firstRank: number = timeframeDisplayRank(first.name);
  const secondRank: number = timeframeDisplayRank(second.name);

  return firstRank - secondRank;
};

const sortTimeframesIntoDayOrder = (
  availableDays: IAvailableServiceTimeDay[]
): IAvailableServiceTimeDay[] => {
  return availableDays.map((day: IAvailableServiceTimeDay) => {
    const copiedTimeframes: IAvailableServiceTimeframe[] = [...day.timeframes];

    const orderedTimeframes: IAvailableServiceTimeframe[] =
      copiedTimeframes.sort(byTimeframeDisplayOrder);

    return { ...day, timeframes: orderedTimeframes };
  });
};

const buildCalendlyEmbedUrl = (
  schedulingUrl: string,
  propertyManager: any
): string => {
  if (!schedulingUrl) {
    return '';
  }

  const embedParams: URLSearchParams = new URLSearchParams({
    embed_domain: window.location.hostname,
    embed_type: 'Inline',
    name: propertyManager?.name ?? '',
    email: propertyManager?.email ?? '',
  });

  return `${schedulingUrl}?${embedParams.toString()}`;
};

export {
  isWithinPreferredTimes,
  toAvailabilityMode,
  sortTimeframesIntoDayOrder,
  buildCalendlyEmbedUrl,
};